import { ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit, inject } from '@angular/core'
import { LoggerFactory } from '@elderbyte/ts-logger'
import { BehaviorSubject, combineLatest, Observable, Subject } from 'rxjs'
import { ActivatedRoute, Router } from '@angular/router'
import { filter, map, startWith, takeUntil } from 'rxjs/operators'
import { PackageService } from '../../packages/package.service'
import {
  DataContextBuilder,
  ElderToastService,
  IDataContext,
  ElderToolbarColumnDirective,
  ElderThemeDirective,
  ElderHeaderComponent,
  ElderShellSlotDirective,
  ElderTableComponent,
  ElderTableToolbarDirective,
  ElderSearchBoxComponent,
  ElderSearchContextDirective,
  ElderFormFieldDenseDirective,
  ElderSinglePaneWrapperComponent,
} from '@elderbyte/ngx-starter'
import { Package } from '../../packages/package'
import { StoragePath } from '../../storage-path'
import { StorageGroup } from '../../storagegroups/storage-group'
import { StorageGroupService } from '../../storagegroups/storage-group.service'
import { StorageOperationsService } from '../../operations/storage-operations.service'
import { FacilityService } from '../../../facilities/facility.service'
import { coerceBooleanProperty } from '@angular/cdk/coercion'
import { MatDialog } from '@angular/material/dialog'
import { StorageManagerDetailDialogComponent } from '../storage-manager-detail-dialog/storage-manager-detail-dialog.component'
import { ElderTranslatePipe } from '@elderbyte/ngx-starter'
import { MatChipListbox, MatChipOption } from '@angular/material/chips'
import {
  MatColumnDef,
  MatHeaderCellDef,
  MatHeaderCell,
  MatCellDef,
  MatCell,
} from '@angular/material/table'
import { MatSlideToggle } from '@angular/material/slide-toggle'
import { MatIcon } from '@angular/material/icon'
import { MatIconButton, MatButton } from '@angular/material/button'
import { FormsModule } from '@angular/forms'
import { MatInput } from '@angular/material/input'
import { MatFormField, MatLabel, MatPrefix, MatSuffix } from '@angular/material/form-field'
import { MatSort, MatSortHeader } from '@angular/material/sort'
import { MatToolbar } from '@angular/material/toolbar'
import { FacilityMenuComponent } from '../../../facilities/facility-menu/facility-menu.component'
import { NgTemplateOutlet, AsyncPipe, DatePipe } from '@angular/common'

export class TerminalCtx {
  constructor(
    public storageScope: StoragePath,
    public kioskMode: boolean
  ) {}
}

export class SearchCtx {
  constructor(
    public storageScope: StoragePath,
    public storageGroup: StorageGroup
  ) {}
}

@Component({
  selector: 'app-storage-manager',
  templateUrl: './storage-manager.component.html',
  styleUrls: ['./storage-manager.component.scss'],
  imports: [
    ElderToolbarColumnDirective,
    FacilityMenuComponent,
    MatToolbar,
    ElderThemeDirective,
    NgTemplateOutlet,
    ElderHeaderComponent,
    ElderShellSlotDirective,
    ElderTableComponent,
    MatSort,
    ElderTableToolbarDirective,
    ElderSearchBoxComponent,
    ElderSearchContextDirective,
    MatFormField,
    ElderFormFieldDenseDirective,
    MatLabel,
    MatPrefix,
    MatInput,
    FormsModule,
    MatIconButton,
    MatSuffix,
    MatIcon,
    MatSlideToggle,
    MatButton,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCellDef,
    MatCell,
    MatSortHeader,
    MatChipListbox,
    MatChipOption,
    AsyncPipe,
    DatePipe,
    ElderTranslatePipe,
    ElderSinglePaneWrapperComponent,
  ],
})
export class StorageManagerComponent implements OnInit, OnDestroy {
  /***************************************************************************
   *                                                                         *
   * Fields                                                                  *
   *                                                                         *
   **************************************************************************/

  private readonly log = LoggerFactory.getLogger(this.constructor.name)

  public readonly kioskMode$ = new BehaviorSubject<boolean>(false)
  public readonly exactStorageScope$ = new BehaviorSubject<StoragePath>(null)

  public readonly storageScope$: Observable<StoragePath>

  public readonly terminalCtx$ = new BehaviorSubject<TerminalCtx>(null)

  public readonly storageGroup$ = new BehaviorSubject<StorageGroup>(null)
  public readonly package$ = new BehaviorSubject<Package>(null)

  public readonly searchCtx$ = new BehaviorSubject<SearchCtx>(null)

  public readonly destroy$ = new Subject<void>()

  public readonly data: IDataContext<StorageGroup>

  public readonly packageNumberPrefix$ = new BehaviorSubject<string>(null)

  // UI input model
  public storageGroupId: string
  public packageNumberInput: string
  public packageNumberPrefixEnabled = true

  private storageGroupService = inject(StorageGroupService)
  private toastService = inject(ElderToastService)
  private dialog = inject(MatDialog)

  /***************************************************************************
   *                                                                         *
   * Constructor                                                             *
   *                                                                         *
   **************************************************************************/

  constructor() {
    const route = inject(ActivatedRoute)
    const storageGroupService = this.storageGroupService
    const facilityService = inject(FacilityService)
    const packageService = inject(PackageService)
    this.kioskMode = coerceBooleanProperty(route.snapshot.queryParams['kioskMode'])
    this.storageScope = route.snapshot.queryParams['storageScope'] as string

    this.storageScope$ = combineLatest([
      facilityService.activeFacility$.pipe(startWith(null)), // important for combineLatest to work here
      this.exactStorageScope$,
    ]).pipe(
      takeUntil(this.destroy$),
      filter(([facility, exactStorageScope]) => !!facility || !!exactStorageScope),
      map(([facility, exactStorageScope]) => {
        return !!exactStorageScope ? exactStorageScope : StoragePath.parse(facility.id)
      })
    )

    combineLatest([this.kioskMode$, this.storageScope$])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([kioskMode, storageScope]) => {
        const ctx = storageScope ? new TerminalCtx(storageScope, kioskMode) : null
        this.terminalCtx$.next(ctx)
      })

    combineLatest([this.storageScope$, this.storageGroup$])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([storageScope, workOrder]) => {
        const ctx = storageScope && workOrder ? new SearchCtx(storageScope, workOrder) : null
        this.searchCtx$.next(ctx)
      })

    packageService
      .getConfig()
      .subscribe((config) => this.packageNumberPrefix$.next(config.numberPrefix))

    this.data = DataContextBuilder.start<StorageGroup>()
      .pageSize(25)
      .reloadOnLocalChanges()
      .build(storageGroupService)
  }

  /***************************************************************************
   *                                                                         *
   * Life Cycle                                                              *
   *                                                                         *
   **************************************************************************/

  public ngOnInit(): void {
    this.data.start([], this.data.filter.filtersSnapshot)
  }

  public ngOnDestroy(): void {
    this.destroy$.next()
    this.destroy$.complete()
  }

  /***************************************************************************
   *                                                                         *
   * Properties                                                              *
   *                                                                         *
   **************************************************************************/

  @Input()
  public set kioskMode(kioskMode: boolean) {
    this.log.info('Setting kioskMode', kioskMode)
    this.kioskMode$.next(kioskMode)
  }

  public get kioskMode(): boolean {
    return this.kioskMode$.getValue()
  }

  @Input()
  public set storageScope(storageScope: string) {
    const path = storageScope ? StoragePath.parse(storageScope) : null
    this.exactStorageScope$.next(path)
  }

  public get canClear(): boolean {
    return !!this.storageGroupId || !!this.packageNumberInput
  }

  /***************************************************************************
   *                                                                         *
   * Public API                                                              *
   *                                                                         *
   **************************************************************************/

  public clear(event: Event): void {
    this.storageGroupId = null
    this.packageNumberInput = null
    this.searchCtx$.next(null)
    this.package$.next(null)
  }

  public openDetail(storageGroup: StorageGroup): Observable<any> {
    this.log.info('exactStorageScope: ', this.exactStorageScope$.getValue())
    const dialogRef = this.dialog.open(StorageManagerDetailDialogComponent, {
      maxWidth: '100vw',
      maxHeight: '100vh',
      height: '100%',
      width: '100%',
      data: {
        storageGroup: storageGroup,
        storageScope: this.exactStorageScope$.getValue()
          ? this.exactStorageScope$.getValue()
          : new StoragePath(null),
      },
    })

    return dialogRef.afterClosed()
  }

  public findByPackageNumber(packageNumberInput: string) {
    let packageNumber = packageNumberInput

    if (this.packageNumberPrefixEnabled) {
      const prefix = this.packageNumberPrefix$.getValue()
      if (prefix) {
        packageNumber = prefix + packageNumberInput
      }
    }

    this.storageGroupService.findOneByPackageNumber(packageNumber).subscribe({
      next: (group) => this.openDetail(group),
      error: (err) => this.toastService.error('Could not find storage group', err),
    })
  }

  /***************************************************************************
   *                                                                         *
   * Private Methods                                                         *
   *                                                                         *
   **************************************************************************/
}
